import { ToastProvider } from '@kk/ui/components/Notifications/ToastProvider';
import i18n from '@kk/ui/i18n';
import { AppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { lazy, Suspense } from 'react';
import { I18nProvider } from 'react-aria';
import { useHotkeys } from 'react-hotkeys-hook';
import { I18nextProvider } from 'react-i18next';
import { useSnapshot } from 'valtio';
import { appInsightsPlugin } from '@/app-insights';
import { defaultNS } from '@/locales/resources';
import state from '@/state';
import queryClient from './api/queryClient';
import { ErrorBoundary } from './components/ErrorBoundary/ErrorBoundary';
import Router from './Router';

// lazy load the devtools in production
const ReactQueryDevtoolsProduction = lazy(() =>
  import('@tanstack/react-query-devtools/production').then((d) => ({
    default: d.ReactQueryDevtools,
  })),
);

function App() {
  const { devtoolsEnabled } = useSnapshot(state);

  // toggle devtools with ctrl+shift+f1 when on production, for debugging purposes
  useHotkeys('ctrl+shift+f1', () => {
    state.devtoolsEnabled = !devtoolsEnabled;
  }, [devtoolsEnabled]);

  return (
    <AppInsightsContext.Provider value={appInsightsPlugin}>
      <I18nextProvider i18n={i18n} defaultNS={defaultNS}>
        <I18nProvider locale="da-DK">
          <ErrorBoundary inline={false}>
            <QueryClientProvider client={queryClient}>
              {import.meta.env.DEV && devtoolsEnabled ? (
                <ReactQueryDevtools initialIsOpen={false} theme="light" />
              ) : null}
              {devtoolsEnabled ? (
                <Suspense fallback={null}>
                  <ReactQueryDevtoolsProduction theme="light" />
                </Suspense>
              ) : null}
              <ToastProvider>
                <Router />
              </ToastProvider>
            </QueryClientProvider>
          </ErrorBoundary>
        </I18nProvider>
      </I18nextProvider>
    </AppInsightsContext.Provider>
  );
}

export default App;
